import React, { useState, useRef, useEffect } from 'react';
import { Form, Button } from 'reactstrap';
import styles from './VerifyOTP.module.scss';

// Allowed characters in the OTP, kept in sync with the backend alphabet
// in `backend/api/controllers/juror.js` (juror_requestOtp).
// Digits 1–9 + uppercase A–Z excluding 0, I, O to avoid visual ambiguity.
const OTP_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZ';
const OTP_CHAR_REGEX = new RegExp(`^[${OTP_ALPHABET}]$`);
const OTP_FULL_REGEX = new RegExp(`^[${OTP_ALPHABET}]{8}$`);

/**
 * VerifyOTP - Screen for entering and verifying OTP code
 */
function VerifyOTP({ email, onOTPVerified, onResendCode, onBackToRequest, loading, countdown }) {
    const [otp, setOtp] = useState(['', '', '', '', '', '', '', '']);
    const [error, setError] = useState('');
    const inputRefs = useRef([]);

    useEffect(() => {
        // Focus first input on mount
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, []);

    const handleChange = (index, value) => {
        const upper = value ? value.toUpperCase() : value;

        if (upper && !OTP_CHAR_REGEX.test(upper)) {
            return;
        }

        const newOtp = [...otp];
        newOtp[index] = upper;
        setOtp(newOtp);
        setError('');

        // Auto-focus next input
        if (upper && index < 7) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        // Handle backspace
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text/plain').trim().toUpperCase();

        if (OTP_FULL_REGEX.test(pastedData)) {
            const newOtp = pastedData.split('');
            setOtp(newOtp);
            setError('');
            // Focus last input
            inputRefs.current[7]?.focus();
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const otpString = otp.join('');

        if (otpString.length !== 8) {
            setError('Bitte geben Sie das vollständige 8-stellige Einmalpasswort ein.');
            return;
        }

        setError('');
        onOTPVerified(otpString);
    };

    const handleResendCode = (e) => {
        e.preventDefault();
        setOtp(['', '', '', '', '', '', '', '']);
        setError('');
        inputRefs.current[0]?.focus();
        if (onResendCode) onResendCode();
    };

    return (
        <div>
            <div className="text-center mb-4">
                <p>
                    Wir haben eine E-Mail mit dem <strong>Einmalpasswort</strong> an <strong>{email}</strong> versandt.
                    <br />
                    Bitte geben Sie das <strong>8-stellige Einmalpasswort</strong> zur Bestätigung ein.
                </p>
            </div>

            <Form onSubmit={handleSubmit}>
                <div
                    className={styles.otpInputGroup}
                    onPaste={handlePaste}
                    role="group"
                    aria-label="Einmalpasswort Eingabe"
                >
                    {otp.map((digit, index) => (
                        <input
                            key={index}
                            ref={(el) => (inputRefs.current[index] = el)}
                            type="text"
                            inputMode="text"
                            autoCapitalize="characters"
                            autoComplete="one-time-code"
                            maxLength={1}
                            value={digit}
                            onChange={(e) => handleChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            className={`${styles.otpInput} ${error ? styles.error : ''}`}
                            aria-label={`Zeichen ${index + 1} von 8`}
                        />
                    ))}
                </div>

                {error && (
                    <div className="alert alert-danger text-center small mt-3 mb-0" role="alert">
                        {error}
                    </div>
                )}

                <div className="d-flex flex-column align-items-center gap-3 mt-4">
                    <Button type="submit" color="secondary" disabled={loading}>
                        {loading ? 'Wird geprüft…' : 'Jetzt anmelden'}
                    </Button>
                    <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={handleResendCode}
                        disabled={loading || countdown > 0}
                        aria-label={
                            countdown > 0 ? `Einmalpasswort erneut senden möglich in ${countdown} Sekunden` : undefined
                        }
                    >
                        {countdown > 0
                            ? `Einmalpasswort erneut senden (${countdown}s)`
                            : 'Einmalpasswort erneut senden'}
                    </button>
                    <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={onBackToRequest}
                        disabled={loading}
                    >
                        E-Mail-Adresse korrigieren
                    </button>
                </div>
            </Form>
        </div>
    );
}

export default VerifyOTP;
